import { SITE_ORIGIN, SITE_NAME, OG_IMAGE_PATH, BOOKING_EMAIL } from './siteMeta';
import { PRIVATE_TRANSFER_PAGE } from './privateTransferPage';

export function organizationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_ORIGIN,
    logo: `${SITE_ORIGIN}/logo512.png`,
    email: BOOKING_EMAIL,
    areaServed: ['Kenya', 'Tanzania'],
  };
}

export function webSiteJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_ORIGIN,
  };
}

export function breadcrumbJsonLd(route) {
  const parentItem =
    route.kind === 'private'
      ? { name: 'Private Transfer', url: `${SITE_ORIGIN}/private-transfer` }
      : { name: 'Bus Tickets', url: `${SITE_ORIGIN}/bus` };

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${SITE_ORIGIN}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: parentItem.name,
        item: parentItem.url,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: route.breadcrumbLabel,
        item: `${SITE_ORIGIN}/${route.slug}`,
      },
    ],
  };
}

export function faqJsonLd(route) {
  if (!route.faqs || route.faqs.length === 0) {
    return null;
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: route.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };
}

export function serviceJsonLd(route) {
  const url = `${SITE_ORIGIN}/${route.slug}`;

  const service = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: route.h1,
    description: route.metaDescription,
    provider: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_ORIGIN,
    },
    areaServed: ['Kenya', 'Tanzania'],
    url,
  };

  if (route.priceFromGbp != null) {
    service.offers = {
      '@type': 'Offer',
      price: String(route.priceFromGbp),
      priceCurrency: 'GBP',
      url,
      availability: 'https://schema.org/InStock',
    };
  }

  return service;
}

export function landingJsonLd(route) {
  return [breadcrumbJsonLd, faqJsonLd, serviceJsonLd]
    .map((fn) => fn(route))
    .filter(Boolean);
}

// ---------------------------------------------------------------------------
// Guide page JSON-LD builders (border crossing, travel guides)
// ---------------------------------------------------------------------------

export function guideBreadcrumbJsonLd(guide) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${SITE_ORIGIN}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: guide.breadcrumbLabel,
        item: `${SITE_ORIGIN}/${guide.slug}`,
      },
    ],
  };
}

export function guideFaqJsonLd(guide) {
  if (!guide.faqs || guide.faqs.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: guide.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };
}

// WebPage schema (no medical/legal guarantee language).
export function guideWebPageJsonLd(guide) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: guide.h1,
    description: guide.metaDescription,
    url: `${SITE_ORIGIN}/${guide.slug}`,
    ...(guide.socialImage && { image: `${SITE_ORIGIN}${guide.socialImage}` }),
    inLanguage: 'en',
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_ORIGIN,
    },
  };
}

export function guideJsonLd(guide) {
  return [guideBreadcrumbJsonLd, guideFaqJsonLd, guideWebPageJsonLd]
    .map((fn) => fn(guide))
    .filter(Boolean);
}

export function privateTransferPageJsonLd() {
  return [faqJsonLd(PRIVATE_TRANSFER_PAGE)].filter(Boolean);
}

export function officesPageJsonLd(page) {
  const org = organizationJsonLd();
  org.contactPoint = page.offices.flatMap((office) =>
    office.phones.map((phone) => ({
      '@type': 'ContactPoint',
      telephone: phone,
      contactType: 'customer service',
      areaServed: office.name.includes('Kenya')
        ? 'KE'
        : office.name.includes('Tanzania')
          ? 'TZ'
          : 'GB',
      availableLanguage: 'English',
    }))
  );
  return [
    org,
    {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: page.h1,
      description: page.metaDescription,
      url: `${SITE_ORIGIN}/${page.slug}`,
      inLanguage: 'en',
    },
  ];
}

// Re-exported for convenience so consumers of this module's JSON-LD builders
// can also access the default social preview image without a second import.
export { OG_IMAGE_PATH };
